import Button from "atoms/buttons/button";
import { ButtonStyles } from "atoms/constants/button-styles";
import { IconSizes } from "atoms/constants/icon-sizes";
import { Icons } from "atoms/constants/icons";
import Icon from "atoms/icons/icon";
import Paragraph from "atoms/typography/paragraph";
import { PermissionScope } from "models/enumerations/notebooks/permission-scope";
import { RecipientType } from "models/enumerations/notebooks/recipient-type";
import NotebookPermissionManifestRecord from "models/view-models/notebooks/notebook-permission-manifest-record";
import NotebookResourcePermissionRecord from "models/view-models/notebooks/notebook-resource-permission-record";
import MenuButton from "molecules/menu-button/menu-button";
import React from "react";
import { CustomColorUtils } from "utilities/custom-color-utils";
import { UserBookmarkColors } from "utilities/enumerations/user-bookmark-colors";
import CultureResources from "utilities/interfaces/culture-resources";
import { t } from "utilities/localization-utils";
import StringUtils from "utilities/string-utils";

interface SharedEntryProps {
    isSelf?: boolean;
    onPermissionChange?: (
        permission: NotebookResourcePermissionRecord,
        scope: PermissionScope
    ) => void;
    onPermissionRemove?: (manifest: NotebookPermissionManifestRecord) => void;
    name: string;
    parentheticalValue: string;
    manifest?: NotebookPermissionManifestRecord;
    scrollableElementRef?: React.RefObject<HTMLDivElement>;
}

const SharedEntryIcon = {
    [RecipientType.Group]: Icons.Team,
    [RecipientType.WorkGroup]: Icons.Team,
};

const PermissionLabel: Record<PermissionScope, keyof CultureResources> = {
    [PermissionScope.View]: "canView",
    // [PermissionScope.Edit]: "canEdit",
};
const CSS_CLASS_NAME = "c-shared-entry";
const SharedEntry = (props: SharedEntryProps) => {
    const {
        isSelf = false,
        onPermissionChange,
        onPermissionRemove,
        name,
        parentheticalValue,
        manifest,
        scrollableElementRef,
    } = props;

    const { permission, recipient } = manifest ?? {};

    const firstInitial = name?.substring(0, 1);

    // Avatar (for owner and individual user saves)
    const colorValue = StringUtils.getRandomNumberBasedOnString(name, 8) + 1; // hash is 0 based, enum is 1 based
    const colorToUseIfIndividual = CustomColorUtils.getClassModifier(
        UserBookmarkColors,
        colorValue,
        "bookmark"
    );

    const handleChangeToView = () => {
        onPermissionChange &&
            onPermissionChange(permission!, PermissionScope.View);
    };

    const handleRemovePermission = () => {
        onPermissionRemove && onPermissionRemove(manifest!);
    };

    return (
        <div className={CSS_CLASS_NAME}>
            <div className={`${CSS_CLASS_NAME}__info-wrapper`}>
                {isSelf || recipient?.type === RecipientType.User ? (
                    <div
                        className={`${CSS_CLASS_NAME}__avatar -circle ${colorToUseIfIndividual}`}>
                        {firstInitial}
                    </div>
                ) : (
                    <Icon
                        cssClassName={`${CSS_CLASS_NAME}__avatar`}
                        type={SharedEntryIcon[recipient?.type!]}
                    />
                )}

                <Paragraph cssClassName={`${CSS_CLASS_NAME}__name`}>
                    {name}{" "}
                    {parentheticalValue && (
                        <span className={`${CSS_CLASS_NAME}__you`}>
                            ({parentheticalValue})
                        </span>
                    )}
                </Paragraph>
            </div>
            {isSelf ? (
                <span className={`${CSS_CLASS_NAME}__owner-scope`}>
                    {t("owner").toLowerCase()}
                </span>
            ) : (
                <div className={`${CSS_CLASS_NAME}__action-wrapper`}>
                    <MenuButton
                        labelOrText={t(
                            PermissionLabel[
                                permission?.permission ?? PermissionScope.View
                            ]
                        )}
                        correctMenuPlacement
                        icon={Icons.ChevronDown}
                        iconSize={IconSizes.Small}
                        useOverlay={false}
                        scrollableElementRef={scrollableElementRef}>
                        <Button
                            disabled // Remove once editing is also an option
                            onClick={handleChangeToView}
                            cssClassName={`${CSS_CLASS_NAME}__action-button -view`}>
                            <span
                                className={`${CSS_CLASS_NAME}__action-button-contents`}>
                                {permission?.permission ===
                                    PermissionScope.View && (
                                    <Icon type={Icons.Checkmark} />
                                )}
                                {t("canView")}
                            </span>
                        </Button>
                        <Button
                            onClick={handleRemovePermission}
                            cssClassName={`${CSS_CLASS_NAME}__action-button -remove`}
                            accessibleText={t("remove")}
                            style={ButtonStyles.Destructive}>
                            <span
                                className={`${CSS_CLASS_NAME}__action-button-contents`}>
                                <Icon type={Icons.Trashcan2} />
                                {t("remove")}
                            </span>
                        </Button>
                    </MenuButton>
                </div>
            )}
        </div>
    );
};

export default SharedEntry;
